import * as R from 'ramda';
import { makeMemoizedGetter, MemoizedGetter } from './data';

export interface RGB {
  r: number;
  g: number;
  b: number;
}

export type HeatMap = MemoizedGetter<number, string>;

// Lighten (percent > 0) or darken (percent < 0) a hex color by a percentage.
export const shadeColor = (color: string, percent: number): string => {
  const f = parseInt(color.slice(1), 16);
  const t = percent < 0 ? 0 : 255;
  const p = percent < 0 ? percent * -1 : percent;
  /* eslint-disable no-bitwise -- hex-color channel extraction */
  const r = f >> 16;
  const g = (f >> 8) & 0x00ff;
  const b = f & 0x0000ff;
  /* eslint-enable no-bitwise */
  return `#${(
    0x1000000 +
    (Math.round((t - r) * p) + r) * 0x10000 +
    (Math.round((t - g) * p) + g) * 0x100 +
    (Math.round((t - b) * p) + b)
  )
    .toString(16)
    .slice(1)}`;
};

// Per-model segment shade of a channel color: model 0 is the darkest, the last model the lightest.
// A fixed range keeps adjacent shades distinguishable and the lightest one legible on white;
// a single model keeps the channel color untouched. Non-hex colors (e.g. rgb()) pass through.
const MODEL_SHADE_DARKEST = -0.25;
const MODEL_SHADE_LIGHTEST = 0.4;
export const getModelShade = (
  channelColor: string,
  modelIndex: number,
  modelCount: number
): string => {
  if (modelCount <= 1 || !/^#[0-9a-fA-F]{6}$/.test(channelColor)) {
    return channelColor;
  }
  const t = Math.min(Math.max(modelIndex, 0), modelCount - 1) / (modelCount - 1);
  return shadeColor(
    channelColor,
    MODEL_SHADE_DARKEST + t * (MODEL_SHADE_LIGHTEST - MODEL_SHADE_DARKEST)
  );
};

// Deterministic djb2-inspired hash, kept in `Number.MAX_SAFE_INTEGER` range via modulo.
const STRING_HASH_MOD = 0x1fffffffffffff; // 2^53 - 1
export const hashString = (input: string): number => {
  let hash = 5381;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 33 + input.charCodeAt(i)) % STRING_HASH_MOD;
  }
  return hash;
};

// Deterministic pastel background from an arbitrary string — same input always maps to the
// same hue, so callers that key on a stable identifier (email, user id) get a persistent color.
// Saturation/lightness are pinned to the pale band that pairs with Brand100 text.
export const stringToPastelHsl = (input: string): string => {
  const hue = input.length === 0 ? 0 : hashString(input) % 360;
  return `hsl(${hue}deg, 90%, 92%)`;
};

// White with transparency
export const WhiteA05 = '#ffffff0d';
export const WhiteA10 = '#ffffff1a';
export const WhiteA15 = '#ffffff26';
export const WhiteA20 = '#ffffff33';
export const WhiteA25 = '#ffffff40';
export const WhiteA30 = '#ffffff4d';

// Black with transparency
export const BlackA05 = '#0000000d';
export const BlackA10 = '#0000001a';
export const BlackA15 = '#00000026';
export const BlackA20 = '#00000033';
export const BlackA25 = '#00000040';
export const BlackA30 = '#0000004d';
export const BlackA50 = '#00000080';

// Primary / Brand (opaque)
export const Brand100 = '#1F003F';
export const Brand90 = '#21005E';
export const Brand80 = '#390094';
export const Brand70 = '#5200CE';
export const Brand60 = '#6B2DEF';
export const Brand50 = '#8254FF';
export const Brand40 = '#977AFF';
export const Brand30 = '#B5A6FF';
export const Brand25 = '#D1CDFF';
export const Brand20 = '#E8E5FF';
export const Brand10 = '#F8F7FF';
export const Brand0 = '#FFFFFF';
export const BrandEquityLight = '#F3DBFF';

// MMM model-type identity palette (multi-model DataQA capsules). A categorical
// set, validated colorblind-safe (adjacent separation) and >= 3:1 contrast on
// white, deliberately outside the reserved Success/Warning/Danger status hues so
// a model capsule never reads as a status. DTC reuses the Brand tokens above;
// each other type has a strong ink + soft tint pair.
export const ModelCommerce = '#0891B2';
export const ModelCommerceSoft = '#CFFAFE';
export const ModelGeo = '#2563EB';
export const ModelGeoSoft = '#DBEAFE';
export const ModelBrandEquity = '#DB2777';
export const ModelBrandEquitySoft = '#FCE7F3';

// Sequential blue ramp (light -> dark) for the geoMMM DMA choropleth — one hue,
// monotonic lightness. `GeoChoroplethNeutral` fills DMAs with no data.
export const GeoChoroplethRamp = ['#DBEAFE', '#BFDBFE', '#93C5FD', '#60A5FA', '#3B82F6', '#1D4ED8'];
export const GeoChoroplethNeutral = '#E2E8F0';

// Brand with transparency
export const Brand70A05 = '#5200ce0d';
export const Brand70A10 = '#5200ce1a';
export const Brand70A15 = '#5200ce26';
export const Brand70A20 = '#5200ce33';
export const Brand70A25 = '#5200ce40';
export const Brand70A30 = '#5200ce4d';
export const Brand40A05 = '#977aff0d';
export const Brand40A10 = '#977aff1a';
export const Brand40A15 = '#977aff26';
export const Brand40A20 = '#977aff33';
export const Brand40A25 = '#977aff40';
export const Brand40A30 = '#977aff4d';

// Scenario-planning delta intensity ramps (change-impact table heat-map + delta text).
// True green/red, distinct from the yellow-green Success* palette. Mirror of
// $ScenarioDeltaGreen*/$ScenarioDeltaRed* in colors.scss.
export const ScenarioDeltaGreen100 = '#05df72';
export const ScenarioDeltaGreen200 = '#00c950';
export const ScenarioDeltaGreen300 = '#00a63e';
export const ScenarioDeltaGreen400 = '#008236';
export const ScenarioDeltaRed100 = '#fb2c36';
export const ScenarioDeltaRed200 = '#c10007';

// Neutral (opaque)
export const Neutral100 = '#FFFFFF';
export const Neutral160 = '#fbfcff';
export const Neutral200 = '#f1f3f9';
export const Neutral300 = '#e1e6ef';
export const Neutral400 = '#cbd2e1';
export const Neutral500 = '#94a0b8';
export const Neutral600 = '#5f6c84';
export const Neutral700 = '#3f444d';
export const Neutral800 = '#23272f';
export const Neutral900 = '#1b1f27';
export const Neutral1000 = '#0a0d14';

// Neutral with transparency
export const Neutral500A05 = '#94a0b80d';
export const Neutral500A10 = '#94a0b81a';
export const Neutral500A15 = '#94a0b826';
export const Neutral500A20 = '#94a0b833';
export const Neutral500A25 = '#94a0b840';
export const Neutral500A30 = '#94a0b84d';

// Success (opaque)
export const Success100 = '#ecf9d9';
export const Success200 = '#dff5bf';
export const Success300 = '#c5ed8c';
export const Success400 = '#a5e24d';
export const Success500 = '#7ed600';
export const Success600 = '#6bb600';
export const Success700 = '#589600';
export const Success800 = '#457600';
export const Success900 = '#325600';

// Success with transparency
export const Success700A05 = '#5896000d';
export const Success700A10 = '#5896001a';
export const Success700A15 = '#58960026';
export const Success700A20 = '#58960033';
export const Success700A25 = '#58960040';
export const Success700A30 = '#5896004d';
export const Success400A05 = '#a5e24d0d';
export const Success400A10 = '#a5e24d1a';
export const Success400A15 = '#a5e24d26';
export const Success400A20 = '#a5e24d33';
export const Success400A25 = '#a5e24d40';
export const Success400A30 = '#a5e24d4d';

// Warning (opaque)
export const Warning100 = '#FFF8EB';
export const Warning200 = '#feebc4';
export const Warning300 = '#fde089';
export const Warning400 = '#fccd5e';
export const Warning500 = '#FBBB3C';
export const Warning600 = '#f59e0b';
export const Warning700 = '#d97706';
export const Warning800 = '#b45309';
export const Warning900 = '#78350f';

// Warning with transparency
export const Warning700A05 = '#d977060d';
export const Warning700A10 = '#d977061a';
export const Warning700A15 = '#d9770626';
export const Warning700A20 = '#d9770633';
export const Warning700A25 = '#d9770640';
export const Warning700A30 = '#d977064d';
export const Warning400A05 = '#fccd5e0d';
export const Warning400A10 = '#fccd5e1a';
export const Warning400A15 = '#fccd5e26';
export const Warning400A20 = '#fccd5e33';
export const Warning400A25 = '#fccd5e40';
export const Warning400A30 = '#fccd5e4d';

// Danger (opaque)
export const Danger100 = '#fde8ea';
export const Danger200 = '#fac5ca';
export const Danger300 = '#f58b94';
export const Danger400 = '#f0515f';
export const Danger500 = '#eb172a';
export const Danger600 = '#c81424';
export const Danger700 = '#a4101d';
export const Danger800 = '#810d17';
export const Danger900 = '#5e0911';

// Danger with transparency
export const Danger600A05 = '#c814240d';
export const Danger600A10 = '#c814241a';
export const Danger600A15 = '#c8142426';
export const Danger600A20 = '#c8142433';
export const Danger600A25 = '#c8142440';
export const Danger600A30 = '#c814244d';
export const Danger400A05 = '#f0515f0d';
export const Danger400A10 = '#f0515f1a';
export const Danger400A15 = '#f0515f26';
export const Danger400A20 = '#f0515f33';
export const Danger400A25 = '#f0515f40';
export const Danger400A30 = '#f0515f4d';

// Channel Indicators
export const Streaming80 = '#6f30ef';
export const Streaming60 = '#a86ff7';
export const Streaming20 = '#d3b7fb';

export const Audio80 = '#1ead93';
export const Audio60 = '#47e6bb';
export const Audio20 = '#b8f5e4';

export const Display80 = '#d5950d';
export const Display60 = '#e8ba25';
export const Display20 = '#fbe8a9';

export const Linear80 = '#06a750';
export const Linear60 = '#67ed22';
export const Linear20 = '#b0fdca';

// Pacing palette shared by the Linear and Streaming Pacing pages: the closest token (CIEDE2000) to
// each bpm-frontend pacing legend color, so migrated pages keep the bar colors users already know.
export const PacingBarColors = {
  deliveryUnsecured: Audio80,
  deliverySecured: Brand80,
  deliveryUpfront: Danger600,
  pacing: Neutral700,
  booked: Warning500,
  clientTarget: Linear80,
  bookedTarget: Streaming60,
  spendOverride: Streaming20
};
// Indexed by broadcast quarter (0-3).
export const PacingQuarterColors = [Neutral200, Linear60, Audio80, Warning600];

/*  Graph Visualization Palette A
    (for 1–8 metrics)

    This palette uses brand colors but is also accessible. This is the default palette for all visualizations. 
*/
export const Channel1 = '#2AE0F6';
export const Channel2 = '#9EF04E';
export const Channel3 = '#E7EE2B';
export const Channel4 = '#0194FF';
export const Channel5 = '#FF5275';
export const Channel6 = '#FF7342';
export const Channel7 = '#BD32FF';
export const Channel8 = '#543CCE';

export const PRIMARY_VISUALIZATION_COLORS = [
  Channel1,
  Channel2,
  Channel3,
  Channel4,
  Channel5,
  Channel6,
  Channel7,
  Channel8
] as const;

export const Channel9a = '#009D9A';
export const Channel10a = '#F9C683';
export const Channel11a = '#E39BC3';

// On tinuititest, we want to show a few extra channels but still use the primary visualization colors
const TEST_ACCOUNT_COLORS = [
  ...PRIMARY_VISUALIZATION_COLORS,
  Channel9a,
  Channel10a,
  Channel11a
] as const;

/*  Graph Visualization Palette B
    (for 9–20 metrics)

    When a graph shows 9–20 metrics, the color palette should change from the default palette to the B palette.
    This palette comes from IBM’s carbon design with a few slight adjustments to make it more Tinuiti branded. 
*/
export const Channel1b = '#543CCE';
export const Channel2b = '#0194FF';
export const Channel3b = '#005D5D';
export const Channel4b = '#9F1853';
export const Channel5b = '#FF5275';
export const Channel6b = '#520408';
export const Channel7b = '#197F38';
export const Channel8b = '#002D9D';
export const Channel9b = '#EE5398';
export const Channel10b = '#B38702';
export const Channel11b = '#009D9A';
export const Channel12b = '#01274A';
export const Channel13b = '#8A3800';
export const Channel14b = '#BD32FF';
export const Channel15b = '#D3CCD8';
export const Channel16b = '#644B6A';
export const Channel17b = '#E39BC3';
export const Channel18b = '#65E4D8';
export const Channel19b = '#E1471C';
export const Channel20b = '#F9C683';

const SECONDARY_VISUALIZATION_COLORS = [
  Channel1b,
  Channel2b,
  Channel3b,
  Channel4b,
  Channel5b,
  Channel6b,
  Channel7b,
  Channel8b,
  Channel9b,
  Channel10b,
  Channel11b,
  Channel12b,
  Channel13b,
  Channel14b,
  Channel15b,
  Channel16b,
  Channel17b,
  Channel18b,
  Channel19b,
  Channel20b
] as const;

// Categorical palette for hash-based avatar fallbacks (e.g. a client or user's
// initial when no photo/brand color is available). Reuses the darker half of
// the secondary visualization palette above — filtered down to the entries
// with enough contrast to hold white avatar text — rather than introducing a
// new set of colors.
export const AvatarColors = [
  Channel1b,
  Channel2b,
  Channel3b,
  Channel4b,
  Channel6b,
  Channel7b,
  Channel8b,
  Channel9b,
  Channel10b,
  Channel11b,
  Channel12b,
  Channel13b,
  Channel14b,
  Channel16b,
  Channel19b
];

/*  Graph Visualization Palette C
    (for 21+ metrics)

    If a visualization uses 21 or more colors, the first 20 metrics should use the visualization
    palette B. Additional metrics colors can be random. When this happens, it is imperative that
    uses can interact with the graph key to narrow in on one metric at a time. Otherwise, it could
    be nearly impossible to distinguish certain colors from one another.
*/

// Only the 100 rung is still read from TS; the intermediate rungs had no consumers. The SCSS twins
// ($GreenHeat100/$GreenHeat60 in colors.scss) are separate and still live.
export const GreenHeat100 = '#15c45b';

/* Heat-map ramps
   ─────────────────────────────────────────────────────────────────────────────────────────────
   Two scales, each authored as 10 discrete stops per arm rather than a pair of poles blended
   continuously. Quantizing means every cell in a column lands on one of a fixed set of shades, so
   two close-but-unequal values read as the same color instead of as an imperceptible gradient.
   `makeSteppedHeatMap` (below) is what places a value on these arrays.

   Stops are a linear sRGB interpolation between the design-supplied endpoints -- the same math
   `mixColors` applies -- so these ramps stay consistent with anything still blending by hand. */

// Sequential: magnitude only, no meaningful zero. Light (low) -> dark (high).
export const SequentialGreenLight = '#d1ffdd';
export const SequentialGreenDark = '#4bda6f';
export const SequentialGreenRamp = [
  SequentialGreenLight,
  '#c2fbd1',
  '#b3f7c5',
  '#a4f3b8',
  '#95efac',
  '#87eaa0',
  '#78e694',
  '#69e287',
  '#5ade7b',
  SequentialGreenDark
] as const;

// Divergent: a signed scale with a meaningful anchor (zero, a mean, a blended benchmark). Green is
// the favorable arm and red the unfavorable one; which end of the DOMAIN each arm covers flips with
// the column's direction, so a cost metric shades green at its low end. The two arms meet at their
// light ends with no neutral stop between them -- a value at the anchor is faintly tinted, never
// white, so it stays distinguishable from a cell that is not heat-mapped at all.
export const DivergentGreenDark = '#8bd027';
export const DivergentGreenLight = '#f4ffe6';
export const DivergentRedLight = '#ffd8dc';
export const DivergentRedDark = '#ff6666';

// Authored outward-in from each extreme, the direction the ramps were specified in.
export const DivergentGreenRamp = [
  DivergentGreenDark,
  '#97d53c',
  '#a2da51',
  '#aee067',
  '#bae57c',
  '#c5ea91',
  '#d1efa6',
  '#ddf5bc',
  '#e8fad1',
  DivergentGreenLight
] as const;

export const DivergentRedRamp = [
  DivergentRedLight,
  '#ffcbcf',
  '#ffbfc2',
  '#ffb2b5',
  '#ffa5a8',
  '#ff999a',
  '#ff8c8d',
  '#ff7f80',
  '#ff7373',
  DivergentRedDark
] as const;

// The two arms as one list ordered worst -> best, which is the order `makeSteppedHeatMap` wants
// (min pole first) for a column where higher is better. Reverse it when lower is better.
export const DivergentColorMap20: readonly string[] = [
  ...[...DivergentRedRamp].reverse(),
  ...[...DivergentGreenRamp].reverse()
];

// Convergent heat-map ramp (light brand → deep brand)
// Gradient reads: Convergent100 → Convergent600
export const Convergent100 = Brand20;
export const Convergent200 = Brand30;
export const Convergent300 = Brand40;
export const Convergent400 = Brand50;
export const Convergent500 = Brand60;
export const Convergent600 = Brand70;

export const ConvergentColorMap6 = [
  Convergent100,
  Convergent200,
  Convergent300,
  Convergent400,
  Convergent500,
  Convergent600
];

// Divergent heat-map ramp (negative = red, positive = teal)
// Gradient reads: DivergentNegative400 → DivergentNegative100 → DivergentPositive100 → DivergentPositive400
export const DivergentNegative100 = '#ffe5e6';
export const DivergentNegative200 = '#ffb1b0';
export const DivergentNegative300 = '#ff726e';
export const DivergentNegative400 = '#fd2c29';

export const DivergentPositive100 = '#ccfaff';
export const DivergentPositive200 = '#94eefa';
export const DivergentPositive300 = '#45d9ed';
export const DivergentPositive400 = '#15c4db';

// Cross-channel goal pacing (/pacing): the bars' delivered/projected/remainder fills, the budget
// marker, and the Details chart's projected-ideal bar. `budget` is mirrored in colors.scss as
// $GoalPacingBudget for the bars legend.
export const GoalPacingColors = {
  delivered: Channel1,
  projected: DivergentPositive200,
  remainder: DivergentPositive100,
  budget: Channel11b,
  idealProjected: Success200
} as const;

export const DivergentColorMap8 = [
  DivergentNegative400,
  DivergentNegative300,
  DivergentNegative200,
  DivergentNegative100,
  DivergentPositive100,
  DivergentPositive200,
  DivergentPositive300,
  DivergentPositive400
];

export const pieSeries = [
  '#1F4E79', // bahamaBlue
  '#548235', // greenLeaf
  '#E5AC00', // orange
  '#C00000', // freeSpeechRed
  '#44546A' // eastBay
];

export const CALENDAR_COLORS = {
  0: '#ECEEEF', // neutralColor
  1: '#01FF70', // springGreen
  2: '#39CCCC', // turquoise
  3: '#FF851B' // pumpkinOrange
};

// If the number of items that we are visualizing together is less than or equal to the number of
// primary colors, use the primary color palette. Otherwise, use the secondary color palette.
export const shouldUsePrimaryPalette = (itemLength: number, usePrimaryColors = true): boolean =>
  itemLength <= PRIMARY_VISUALIZATION_COLORS.length && usePrimaryColors;

// Get the color to use based on item index and the color palette we're using.
const overflowChannelColors: Record<string, string> = {};

export const randomColorPart = (): string =>
  Math.floor(Math.random() * 256)
    .toString(16)
    .padStart(2, '0');

export const getChannelSeriesColor = (
  i: number,
  usePrimaryPalette = true,
  useTestAccountPalette = false
): string => {
  const ourColors = (() => {
    if (useTestAccountPalette) {
      return TEST_ACCOUNT_COLORS;
    }

    if (usePrimaryPalette) {
      return PRIMARY_VISUALIZATION_COLORS;
    }

    return SECONDARY_VISUALIZATION_COLORS;
  })();

  if (i < ourColors.length) {
    return ourColors[i];
  }
  if (overflowChannelColors[`${i}`]) {
    return overflowChannelColors[`${i}`];
  }
  const newRandomColor = `#${randomColorPart()}${randomColorPart()}${randomColorPart()}`;
  overflowChannelColors[`${i}`] = newRandomColor;
  return newRandomColor;
};

export const getColorMap = (
  data: { name: string; [key: string]: any }[],
  hardCodedColorName?: string,
  hardCodedColor?: string,
  usePrimaryColors?: boolean
): Record<string, string> | any => {
  if (R.isEmpty(data)) {
    return {};
  }

  const usePrimaryPalette = shouldUsePrimaryPalette(data.length, usePrimaryColors);
  const sortedData = R.sortBy(R.compose(R.toLower, R.prop('name')))(data);

  return sortedData.reduce((prev, curr, i) => {
    if (curr.name === hardCodedColorName) {
      return { ...prev, [curr.name]: hardCodedColor };
    }
    return { ...prev, [curr.name]: getChannelSeriesColor(i, usePrimaryPalette) };
  }, {});
};

interface CustomColorMapping {
  name: string;
  color: string;
}

export const customColorMapping = (
  data: any[],
  colorMappings?: CustomColorMapping[],
  usePrimaryColors?: boolean
): any => {
  if (R.isEmpty(data)) {
    return {};
  }

  const usePrimaryPalette = shouldUsePrimaryPalette(data.length, usePrimaryColors);
  const sortedData = R.sortBy(R.compose(R.toLower, R.prop('name')))(data);

  return sortedData.reduce((prev, curr, i) => {
    const mapping = colorMappings?.find(colorMapping => colorMapping.name === curr.name);
    if (mapping) {
      return { ...prev, [curr.name]: mapping.color };
    }
    return { ...prev, [curr.name]: getChannelSeriesColor(i, usePrimaryPalette) };
  }, {});
};

export const mixColors = (color1: RGB, color2: RGB, pct: number): RGB => ({
  r: Math.round((1 - pct) * color1.r + pct * color2.r),
  g: Math.round((1 - pct) * color1.g + pct * color2.g),
  b: Math.round((1 - pct) * color1.b + pct * color2.b)
});

export const rgbToHex = ({ r, g, b }: RGB): string =>
  `#${[r, g, b].map(n => Math.floor(n).toString(16).padStart(2, '0')).join('')}`;

// The company API returns '#000000' as an unset placeholder rather than
// omitting the field, so treat pure-black (in any hex shorthand) as absent.
export const hasCustomColor = (color?: string): color is string => {
  if (!color) return false;
  return !/^#0+$/i.test(color.trim());
};

export const hexToRgb = (hex: string): RGB => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return { r: 0, g: 0, b: 0 };
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  };
};

// Picks the readable text color for an arbitrary background — used wherever a
// categorical palette color lands behind text (series pills, timeline bars), so
// the text does not have to assume a light or dark palette. Per ITU-R BT.709.
export const getContrastTextColor = (backgroundHex: string): string => {
  const { r, g, b } = hexToRgb(backgroundHex);
  const luma = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luma > 0.4 ? Neutral800 : Neutral160;
};

/**
 * Places `value` on one of `stops` by its fractional position in [lo, hi]. A zero-width range has
 * no gradient to walk, so it saturates to the `hi` pole rather than dividing by zero.
 */
const pickStop = (stops: readonly string[], lo: number, hi: number, value: number): string => {
  const last = stops[stops.length - 1];
  if (hi <= lo) return last;
  const t = Math.max(0, Math.min(1, (value - lo) / (hi - lo)));
  // `floor(t * length)` buckets evenly; only t === 1 overflows, hence the clamp to the last index.
  return stops[Math.min(stops.length - 1, Math.floor(t * stops.length))] ?? last;
};

interface SteppedHeatScale {
  min?: number;
  max?: number;
  /** The anchor of a two-armed (divergent) scale. Omit for a single-armed (sequential) one. */
  midpoint?: number;
  /** Stops ordered from the `min` pole to the `max` pole. A divergent scale splits them in half. */
  stops: readonly string[];
}

/**
 * QUANTIZES `value` onto one of `stops` rather than blending between poles, so a column renders in
 * a fixed, countable set of shades and two close-but-unequal values read as the same color. Values
 * outside [min, max] clamp to the end stops.
 *
 * Given a `midpoint`, the stop list splits down the middle and each half spans its own arm. That
 * way an asymmetric domain -- an anchor sitting far off-center, which is the norm for a mean or a
 * blended benchmark -- still gets the full stop count on both sides, and the anchor always lands on
 * the seam between the two innermost stops.
 *
 * Prefer `makeSteppedHeatMap` where the scale can be built once per column; reach for this directly
 * only on a per-cell path that has nowhere to hoist the scale to.
 */
export const getSteppedHeatColor = (
  { min = 0, max = 100, midpoint, stops }: SteppedHeatScale,
  value: number
): string => {
  // `midpoint === undefined`, not a falsy check: 0 is a real and common anchor (every signed delta
  // or variance metric centers there) and must not be read as "no midpoint given".
  if (midpoint === undefined) return pickStop(stops, min, max, value);
  const half = Math.floor(stops.length / 2);
  return value < midpoint
    ? pickStop(stops.slice(0, half), min, midpoint, value)
    : pickStop(stops.slice(half), midpoint, max, value);
};

/**
 * `getSteppedHeatColor` bound to one scale and memoized per value. Build this ONCE per column, not
 * per cell -- a fresh getter per cell would allocate a cache that is thrown away before its second
 * read, which is the opposite of the point.
 */
export const makeSteppedHeatMap = (scale: SteppedHeatScale): HeatMap =>
  makeMemoizedGetter<number, string>({
    calculate: value => getSteppedHeatColor(scale, value)
  });
